import React, { useState, useEffect } from 'react';
import { BASE } from '../utils/api';
import {
  Card,
  CardContent,
  Typography,
  Button,
  CircularProgress,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Paper,
  Box,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Stack,
  InputAdornment,
  SelectChangeEvent,
  Alert
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  Search as SearchIcon,
  Clear as ClearIcon
} from '@mui/icons-material';

interface SwapHistoryItem {
  id: number;
  uid: number;
  from_currency: string;
  to_currency: string;
  from_amount: number;
  to_amount: number;
  fee_percentage: number;
  fee_amount: number;
  fee_currency: string;
  usd_rate_from: number;
  usd_rate_to: number;
  created_at: string;
  user_name: string;
}

type SortField = keyof SwapHistoryItem;

interface SortConfig {
  field: SortField;
  direction: 'asc' | 'desc';
}

const panelSx = {
  backgroundColor: '#232626',
  border: '1px solid #2f3536',
  borderRadius: 3,
  boxShadow: '0 18px 42px rgba(0, 0, 0, 0.28)',
};

const fieldSx = {
  '& .MuiOutlinedInput-root': {
    color: '#e2e8f0',
    backgroundColor: '#232626',
    borderRadius: '12px',
    '& fieldset': {
      borderColor: '#2f3536',
    },
    '&:hover fieldset': {
      borderColor: '#25ee89',
    },
    '&.Mui-focused fieldset': {
      borderColor: '#25ee89',
    },
    '& .MuiSvgIcon-root': {
      color: '#94a3b8',
    },
  },
  '& .MuiInputLabel-root': {
    color: '#94a3b8',
  },
  '& .MuiInputLabel-root.Mui-focused': {
    color: '#25ee89',
  },
};

const SwapHistory: React.FC = () => {
  const [data, setData] = useState<SwapHistoryItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // Filter states
  const [search, setSearch] = useState('');
  const [fromCurrency, setFromCurrency] = useState<string>('');
  const [toCurrency, setToCurrency] = useState<string>('');
  
  // Sorting state
  const [sortConfig, setSortConfig] = useState<SortConfig>({
    field: 'created_at',
    direction: 'desc'
  });

  const fetchData = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${BASE}/internalswap/history`);
      const result = await response.json();
      if (result.success) {
        setData(result.data);
      } else {
        setError('Failed to fetch data');
      }
    } catch (err) {
      setError('Error fetching data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  // Get unique currencies for filters
  const uniqueFromCurrencies = Array.from(new Set(data.map(item => item.from_currency)));
  const uniqueToCurrencies = Array.from(new Set(data.map(item => item.to_currency)));

  // Handle sorting
  const handleSort = (field: SortField) => {
    setSortConfig({
      field,
      direction: sortConfig.field === field && sortConfig.direction === 'asc' ? 'desc' : 'asc'
    });
  };

  // Filter and sort data
  const filteredAndSortedData = [...data]
    .filter(item => {
      const searchLower = search.toLowerCase();
      return (
        (item.user_name?.toLowerCase().includes(searchLower) ||
         item.uid.toString().includes(searchLower)) &&
        (!fromCurrency || item.from_currency === fromCurrency) &&
        (!toCurrency || item.to_currency === toCurrency)
      );
    })
    .sort((a, b) => {
      const aValue = a[sortConfig.field];
      const bValue = b[sortConfig.field];
      
      if (typeof aValue === 'string' && typeof bValue === 'string') {
        return sortConfig.direction === 'asc' 
          ? aValue.localeCompare(bValue)
          : bValue.localeCompare(aValue);
      }
      
      return sortConfig.direction === 'asc'
        ? (aValue as number) - (bValue as number)
        : (bValue as number) - (aValue as number);
    });

  const resetFilters = () => {
    setSearch('');
    setFromCurrency('');
    setToCurrency('');
  };

  const formatAmount = (value: number) => value.toFixed(8);

  if (error) {
    return (
      <Box className="ml-0 lg:ml-[256px] min-h-screen bg-[#1a1d1e] p-4 md:p-6">
        <Alert
          severity="error"
          sx={{
            ...panelSx,
            color: '#fecaca',
            '& .MuiAlert-icon': { color: '#f87171' },
          }}
        >
          {error}
        </Alert>
      </Box>
    );
  }

  return (
    <div className="ml-0 lg:ml-[256px] min-h-screen bg-[#1a1d1e] p-4 md:p-6">
      <Card sx={panelSx}>
        <CardContent sx={{ p: { xs: 2, md: 4 } }}>
          <Stack
            direction={{ xs: 'column', md: 'row' }}
            spacing={2}
            alignItems={{ xs: 'flex-start', md: 'center' }}
            justifyContent="space-between"
            marginBottom={3}
          >
            <Typography variant="h4" component="h2" sx={{ color: '#e2e8f0', fontWeight: 700 }}>
              Swap History
            </Typography>
            <Button
              startIcon={<RefreshIcon />}
              variant="contained"
              onClick={fetchData}
              disabled={loading}
              sx={{
                backgroundColor: '#25ee89',
                color: '#1a1d1e',
                borderRadius: '12px',
                px: 2.5,
                py: 1.25,
                fontWeight: 700,
                boxShadow: 'none',
                '&:hover': {
                  backgroundColor: '#1fcc76',
                  boxShadow: 'none',
                },
                '&.Mui-disabled': {
                  backgroundColor: '#2f3536',
                  color: '#64748b',
                },
              }}
            >
              Refresh
            </Button>
          </Stack>

          <Stack
            direction={{ xs: 'column', lg: 'row' }}
            spacing={2}
            marginBottom={3}
            alignItems="stretch"
          >
            <TextField
              label="Search by Username or UID"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                )
              }}
              fullWidth
              sx={fieldSx}
            />
            
            <FormControl sx={{ minWidth: 180, ...fieldSx }}>
              <InputLabel>From Currency</InputLabel>
              <Select
                value={fromCurrency}
                label="From Currency"
                onChange={(e: SelectChangeEvent) => setFromCurrency(e.target.value)}
              >
                <MenuItem value="">All</MenuItem>
                {uniqueFromCurrencies.map(currency => (
                  <MenuItem key={currency} value={currency}>{currency}</MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl sx={{ minWidth: 180, ...fieldSx }}>
              <InputLabel>To Currency</InputLabel>
              <Select
                value={toCurrency}
                label="To Currency"
                onChange={(e: SelectChangeEvent) => setToCurrency(e.target.value)}
              >
                <MenuItem value="">All</MenuItem>
                {uniqueToCurrencies.map(currency => (
                  <MenuItem key={currency} value={currency}>{currency}</MenuItem>
                ))}
              </Select>
            </FormControl>

            <Button
              startIcon={<ClearIcon />}
              onClick={resetFilters}
              variant="outlined"
              sx={{
                minWidth: 170,
                borderRadius: '12px',
                borderColor: '#25ee89',
                color: '#25ee89',
                fontWeight: 700,
                '&:hover': {
                  borderColor: '#1fcc76',
                  backgroundColor: 'rgba(37, 238, 137, 0.08)',
                },
              }}
            >
              Clear Filters
            </Button>
          </Stack>

          <TableContainer
            component={Paper}
            sx={{
              backgroundColor: '#232626',
              border: '1px solid #2f3536',
              borderRadius: 3,
              overflow: 'hidden',
            }}
          >
            <Table sx={{ minWidth: 960 }}>
              <TableHead sx={{ backgroundColor: '#1fcc76' }}>
                <TableRow>
                  <TableCell sx={{ color: '#ffffff', fontWeight: 700, borderBottomColor: '#1fcc76' }}>
                    <TableSortLabel
                      active={sortConfig.field === 'created_at'}
                      direction={sortConfig.direction}
                      onClick={() => handleSort('created_at')}
                      sx={{
                        color: '#ffffff !important',
                        '& .MuiTableSortLabel-icon': { color: '#ffffff !important' },
                      }}
                    >
                      Date/Time
                    </TableSortLabel>
                  </TableCell>
                  <TableCell sx={{ color: '#ffffff', fontWeight: 700, borderBottomColor: '#1fcc76' }}>
                    <TableSortLabel
                      active={sortConfig.field === 'user_name'}
                      direction={sortConfig.direction}
                      onClick={() => handleSort('user_name')}
                      sx={{
                        color: '#ffffff !important',
                        '& .MuiTableSortLabel-icon': { color: '#ffffff !important' },
                      }}
                    >
                      Username
                    </TableSortLabel>
                  </TableCell>
                  <TableCell sx={{ color: '#ffffff', fontWeight: 700, borderBottomColor: '#1fcc76' }}>UID</TableCell>
                  <TableCell sx={{ color: '#ffffff', fontWeight: 700, borderBottomColor: '#1fcc76' }}>From Currency</TableCell>
                  <TableCell sx={{ color: '#ffffff', fontWeight: 700, borderBottomColor: '#1fcc76' }}>To Currency</TableCell>
                  <TableCell align="right" sx={{ color: '#ffffff', fontWeight: 700, borderBottomColor: '#1fcc76' }}>From Amount</TableCell>
                  <TableCell align="right" sx={{ color: '#ffffff', fontWeight: 700, borderBottomColor: '#1fcc76' }}>To Amount</TableCell>
                  <TableCell align="right" sx={{ color: '#ffffff', fontWeight: 700, borderBottomColor: '#1fcc76' }}>Fee Amount</TableCell>
                  <TableCell align="right" sx={{ color: '#ffffff', fontWeight: 700, borderBottomColor: '#1fcc76' }}>Fee %</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={9} align="center" sx={{ color: '#94a3b8', borderBottomColor: '#2f3536', py: 6 }}>
                      <CircularProgress sx={{ color: '#25ee89' }} />
                    </TableCell>
                  </TableRow>
                ) : filteredAndSortedData.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={9} align="center" sx={{ color: '#94a3b8', borderBottomColor: '#2f3536', py: 6 }}>
                      No data found
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredAndSortedData.map((row) => (
                    <TableRow
                      key={row.id}
                      hover
                      sx={{
                        backgroundColor: '#232626',
                        '&:hover': {
                          backgroundColor: '#1f2324',
                        },
                        '& td': {
                          borderBottomColor: '#2f3536',
                          color: '#94a3b8',
                        },
                      }}
                    >
                      <TableCell sx={{ color: '#e2e8f0 !important', fontWeight: 500 }}>
                        {new Date(row.created_at).toLocaleString()}
                      </TableCell>
                      <TableCell sx={{ color: '#e2e8f0 !important', fontWeight: 600 }}>{row.user_name}</TableCell>
                      <TableCell>{row.uid}</TableCell>
                      <TableCell>
                        <Chip
                          label={row.from_currency}
                          variant="outlined"
                          sx={{
                            color: '#25ee89',
                            borderColor: '#25ee89',
                            backgroundColor: 'rgba(37, 238, 137, 0.08)',
                            fontWeight: 700,
                          }}
                        />
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={row.to_currency}
                          variant="outlined"
                          sx={{
                            color: '#7dd3fc',
                            borderColor: '#334155',
                            backgroundColor: 'rgba(125, 211, 252, 0.08)',
                            fontWeight: 700,
                          }}
                        />
                      </TableCell>
                      <TableCell align="right">
                        {formatAmount(row.from_amount)}
                      </TableCell>
                      <TableCell align="right">
                        {formatAmount(row.to_amount)}
                      </TableCell>
                      <TableCell align="right">
                        {formatAmount(row.fee_amount)}
                      </TableCell>
                      <TableCell align="right">
                        {(row.fee_percentage * 100).toFixed(2)}%
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>
    </div>
  );
};

export default SwapHistory;
